import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PackageSelector, { PackageData } from '../components/PackageSelector';
import PreRegEmailVerify from '../components/PreRegEmailVerify';
import PreRegPhoneVerify from '../components/PreRegPhoneVerify';
import { useAppConfig } from '../hooks/useAppConfig';
import api from '../services/api';
import './Register.css';

interface PaymentMethod {
  id: number;
  name: string;
  nameAr: string;
  icon: string;
}

interface Profession {
  id: number;
  name: string;
  nameAr: string;
}

interface Wilaya {
  id: number;
  code: string;
  name: string;
  nameAr: string;
}

function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isArabic = i18n.language === 'ar';

  // Multi-step state
  const [step, setStep] = useState(1);

  // Pre-registration verification (#105) — sub-steps within step 1
  const [subStep1, setSubStep1] = useState<'verify-email' | 'verify-phone' | 'fill-info'>('verify-email');
  const [emailToken, setEmailToken] = useState('');
  const [phoneToken, setPhoneToken] = useState('');

  // #107 — SMS_ENABLED toggle
  const { config: appConfig } = useAppConfig();

  const restartVerification = () => {
    setEmailToken('');
    setPhoneToken('');
    setEmail('');
    setPhone('');
    setSubStep1('verify-email');
  };

  // User info state - Step 2
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState<number | null>(null);
  const [professionOther, setProfessionOther] = useState('');
  const [structure, setStructure] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [selectedWilaya, setSelectedWilaya] = useState<number | null>(null);
  const [territoire, setTerritoire] = useState('');

  const [acceptedCgv, setAcceptedCgv] = useState(false);

  // Subscription state
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [testPackage, setTestPackage] = useState<PackageData | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [nbPoste, setNbPoste] = useState<number | null>(null);
  const [allPackages, setAllPackages] = useState<PackageData[]>([]);

  // Data state (packages are now fetched by PackageSelector component)
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Payment & Terms
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(null);
  const [termsError, setTermsError] = useState('');

  // Status state
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showChargilyRedirect, setShowChargilyRedirect] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

  // Fetch reference data on mount (packages are fetched by PackageSelector)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [professionsRes, wilayasRes, paymentMethodsRes] = await Promise.all([
          api.get('/professions'),
          api.get('/wilayas'),
          api.get('/payment-methods')
        ]);
        setProfessions(professionsRes.data || []);
        setWilayas(wilayasRes.data || []);
        setPaymentMethods(paymentMethodsRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();

    // Check for test plan
    const planParam = searchParams.get('plan');
    if (planParam === 'test') {
      const fetchTestPackage = async () => {
        try {
          const res = await api.get('/packages/test');
          if (res.data) {
            setTestPackage(res.data);
            setSelectedPlan(res.data.id);
            setIsTestMode(true);
            // Optionally auto-advance to step 2? The user requirement says "le pack test sera selectionné et transmis à la page d'inscriptions"
            // Let's keep step 1 but show the test package clearly
          }
        } catch (err) {
          console.error('Error fetching test package:', err);
        }
      };
      fetchTestPackage();
    }
  }, [searchParams]);

  // Set default payment method for test mode
  useEffect(() => {
    if (isTestMode && paymentMethods.length > 0 && !selectedPaymentMethod) {
      const carteBancaire = paymentMethods.find(m =>
        m.name.toLowerCase().includes('carte') ||
        m.name.toLowerCase().includes('cib') ||
        m.name.toLowerCase().includes('bancaire')
      );
      if (carteBancaire) {
        setSelectedPaymentMethod(carteBancaire.id);
      } else {
        setSelectedPaymentMethod(paymentMethods[0].id);
      }
    }
  }, [isTestMode, paymentMethods, selectedPaymentMethod]);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 10,
    hasUppercase: /[A-Z]/.test(password),
    hasDigit: /[0-9]/.test(password),
  };
  const isPasswordValid = passwordRules.minLength && passwordRules.hasUppercase && passwordRules.hasDigit;

  const handleRegister = async () => {
    setTermsError('');
    setErrorMessage('');

    if (!selectedPlan) {
      setErrorMessage(t('register.errors.selectPackage'));
      return;
    }

    if (!email || !first_name || !last_name || !phone || !password) {
      setErrorMessage(t('register.errors.fillRequired'));
      return;
    }

    if (!isPasswordValid) {
      setErrorMessage(t('register.errors.passwordWeak'));
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(t('register.errors.passwordMismatch'));
      return;
    }

    if (!acceptedCgv) {
      setTermsError(t('register.errors.acceptTerms'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          first_name,
          last_name,
          password,
          profession_id: selectedProfession,
          profession_other: professionOther,
          structure,
          address,
          city: city || null,
          postal_code: postalCode || null,
          wilaya_id: selectedWilaya,
          phone,
          territoire: territoire || null,
          package_id: selectedPlan,
          billing_period: billingPeriod,
          payment_method_id: selectedPaymentMethod,
          nb_poste: nbPoste,
          preferred_language: i18n.language === 'ar' ? 'ar' : 'fr',
          email_token: emailToken,
          phone_token: phoneToken
        }),
      });

      if (response.ok) {
        const result = await response.json();

        // Essai gratuit : compte activé d'office → connexion directe
        if (result.is_trial) {
          if (result.access_token) {
            localStorage.setItem('token', result.access_token);
            navigate(`/${i18n.language}/chat`);
            return;
          }
          setSuccessMessage(t('auth.trialActivated'));
          setErrorMessage('');
          setIsDisabled(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Check if payment method is "Carte bancaire" → redirect to Chargily
        const isCardPayment = paymentMethods.find(m => m.id === selectedPaymentMethod);
        const isCard = isCardPayment && (
          isCardPayment.name.toLowerCase().includes('carte') ||
          isCardPayment.name.toLowerCase().includes('cib')
        );

        if (isCard && result.payment_id && result.payment_status === 'pending') {
          setShowChargilyRedirect(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Get the price for the selected package
          const pkg = allPackages.find(p => p.id === selectedPlan);
          const amount = billingPeriod === 'annual' && pkg?.yearlyPrice
            ? pkg.yearlyPrice
            : pkg?.price || 0;

          try {
            const checkoutRes = await api.post('/chargily/checkout', {
              payment_id: result.payment_id,
              amount: amount,
              description: `${result.package_name} - ${result.email}`,
              lang: i18n.language,
            });
            if (checkoutRes.data?.checkout_url) {
              window.location.href = checkoutRes.data.checkout_url;
              return;
            }
          } catch (err) {
            console.error('Chargily checkout error:', err);
            setShowChargilyRedirect(false);
            // Fallback to normal success message if Chargily fails
          }
        }

        setSuccessMessage(t('auth.registrationSuccess'));
        setErrorMessage('');
        setIsDisabled(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        let errorMsg = t('auth.registrationFailed');
        try {
          const errorData = await response.json();
          if (errorData.detail === 'User with this email already exists') {
            errorMsg = t('register.errors.emailExists', 'Cet email est déjà associé à un compte. Veuillez vous connecter ou utiliser une autre adresse.');
          } else if (errorData.detail) {
            errorMsg = errorData.detail;
          }
        } catch (e) {
          // ignore JSON parse error
        }
        setErrorMessage(errorMsg);
        setSuccessMessage('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrorMessage(t('auth.registrationError'));
      setSuccessMessage('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };


  const [validatingStep1, setValidatingStep1] = useState(false);

  const handleContinue = async () => {
    setErrorMessage('');
    setValidatingStep1(true);
    try {
      // Email/phone already validated at pre-registration step (#105) — no need to re-check
      // We keep the API call as a defensive measure (e.g., admin created same email between sub-steps)
      const res = await fetch(`${API_BASE}/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.exists) {
        setErrorMessage(t('register.errors.emailExists', 'Cet email est déjà associé à un compte. Veuillez vous connecter ou utiliser une autre adresse.'));
        return;
      }
      setStep(2);
    } catch {
      // If check fails, let them proceed — register endpoint will catch duplicates
      setStep(2);
    } finally {
      setValidatingStep1(false);
    }
  };

  // Check if "Autre" profession is selected
  const isOtherProfession = professions.find(p => p.id === selectedProfession)?.name?.toLowerCase() === 'autre';

  return (
    <div className="register-page">
      <div className="container py-4">
        <div className="register-card" style={{ position: 'relative' }}>
          {/* Loading overlay */}
          {submitting && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.8)', zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 'inherit',
            }}>
              <div className="text-center">
                <div className="spinner-border text-danger" style={{ width: '3rem', height: '3rem' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted fw-semibold">
                  {isArabic ? 'جاري التسجيل...' : 'Inscription en cours...'}
                </p>
              </div>
            </div>
          )}

          {/* Language Switcher */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Link to={`/${i18n.language}`} className="back-link">
              <i className="bi bi-arrow-left me-1"></i>
              {t('register.back')}
            </Link>
            <LanguageSwitcher />
          </div>

          {/* Step Indicator */}
          <div className="step-indicator mb-4">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">{t('register.stepInfo')}</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">{t('register.stepFormula')}</span>
            </div>
          </div>

          {/* Success Popup Modal */}
          {successMessage && (
            <div className="success-modal-overlay">
              <div className="success-modal">
                <button
                  className="success-modal-close"
                  onClick={() => { navigate(`/${i18n.language}`); window.scrollTo(0, 0); }}
                  aria-label="Fermer"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
                <div className="success-modal-icon">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <p className="success-modal-message">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Chargily Redirect Popup */}
          {showChargilyRedirect && (
            <div className="success-modal-overlay">
              <div className="success-modal">
                <div className="success-modal-icon">
                  <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <p className="success-modal-message">
                  {i18n.language === 'ar'
                    ? 'جاري إعادة التوجيه إلى منصة الدفع Chargily...'
                    : 'Redirection vers la plateforme de paiement Chargily...'}
                </p>
                <p className="text-muted small mt-2">
                  {i18n.language === 'ar'
                    ? 'يرجى عدم إغلاق هذه الصفحة'
                    : 'Veuillez ne pas fermer cette page'}
                </p>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {errorMessage && (
            <div className="alert alert-danger mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {errorMessage}
            </div>
          )}

          {/* STEP 1: User Information (with pre-registration sub-steps #105) */}
          {step === 1 && subStep1 === 'verify-email' && (
            <PreRegEmailVerify
              apiBase={API_BASE}
              onVerified={(verifiedEmail, token) => {
                setEmail(verifiedEmail);
                setEmailToken(token);
                // #107 — skip phone substep si SMS désactivé côté serveur
                setSubStep1(appConfig.sms_enabled ? 'verify-phone' : 'fill-info');
              }}
            />
          )}

          {step === 1 && subStep1 === 'verify-phone' && (
            <>
              <PreRegPhoneVerify
                apiBase={API_BASE}
                emailToken={emailToken}
                onVerified={(verifiedPhone, token) => {
                  setPhone(verifiedPhone);
                  setPhoneToken(token);
                  setSubStep1('fill-info');
                }}
              />
              <div className="text-center mt-3">
                <button onClick={restartVerification} className="btn btn-link btn-sm">
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  {t('preReg.restart', "Recommencer (changer l'email)")}
                </button>
              </div>
            </>
          )}

          {step === 1 && subStep1 === 'fill-info' && (
            <>
              <h2 className="text-center mb-4">
                <i className="bi bi-person-badge me-2"></i>
                {t('register.subscriberInfo')}
              </h2>

              <div className="alert alert-success py-2">
                <i className="bi bi-check-circle me-2"></i>
                {appConfig.sms_enabled
                  ? t('preReg.bothVerified', 'Email et téléphone vérifiés.')
                  : t('preReg.emailVerified', 'Email vérifié.')}
                <button onClick={restartVerification} className="btn btn-link btn-sm p-0 ms-2 align-baseline">
                  {t('preReg.changeContact', 'Modifier')}
                </button>
              </div>

              <div className="row g-3">
                {/* Full Name */}
                <div className="col-md-6">
                  <label className="form-label">{t('register.firstName')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={first_name}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={isDisabled}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">{t('register.lastName')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={last_name}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isDisabled}
                    required
                  />
                </div>

                {/* Profession/Statut */}
                <div className="col-12">
                  <label className="form-label">{t('register.profession')}</label>
                  <div className="profession-checkboxes">
                    {professions.map(p => (
                      <label key={p.id} className={`profession-checkbox ${selectedProfession === p.id ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="profession"
                          checked={selectedProfession === p.id}
                          onChange={() => setSelectedProfession(p.id)}
                          disabled={isDisabled}
                        />
                        <span>{isArabic ? p.nameAr : p.name}</span>
                      </label>
                    ))}
                  </div>
                  {isOtherProfession && (
                    <input
                      type="text"
                      className="form-control mt-2"
                      placeholder={t('register.specifyProfession')}
                      value={professionOther}
                      onChange={(e) => setProfessionOther(e.target.value)}
                      disabled={isDisabled}
                    />
                  )}
                </div>

                {/* Structure/Cabinet/Entreprise */}
                <div className="col-12">
                  <label className="form-label">{t('register.structure')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={structure}
                    onChange={(e) => setStructure(e.target.value)}
                    disabled={isDisabled}
                  />
                </div>

                {/* Address */}
                <div className="col-12">
                  <label className="form-label">{t('register.professionalAddress')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    disabled={isDisabled}
                  />
                </div>

                {/* Territoire — radio buttons */}
                <div className="col-12">
                  <label className="form-label">{t('register.territoire')}</label>
                  <div className="d-flex gap-4">
                    <label className={`profession-checkbox ${territoire === 'national' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="territoire"
                        checked={territoire === 'national'}
                        onChange={() => setTerritoire('national')}
                        disabled={isDisabled}
                      />
                      <span>{t('register.territoireNational')}</span>
                    </label>
                    <label className={`profession-checkbox ${territoire === 'etranger' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="territoire"
                        checked={territoire === 'etranger'}
                        onChange={() => setTerritoire('etranger')}
                        disabled={isDisabled}
                      />
                      <span>{t('register.territoireEtranger')}</span>
                    </label>
                  </div>
                </div>

                {/* Conditional: Wilaya | Ville | Code postal — only for National */}
                {territoire === 'national' && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">{t('register.wilaya')}</label>
                      <select
                        className="form-select"
                        value={selectedWilaya || ''}
                        onChange={(e) => setSelectedWilaya(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={isDisabled}
                      >
                        <option value="">{t('register.selectWilaya')}</option>
                        {wilayas.map(w => (
                          <option key={w.id} value={w.id}>
                            {w.code} - {isArabic ? w.nameAr : w.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('city')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={isDisabled}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('postalCode')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </>
                )}

                {/* Téléphone | E-mail — verrouillés si vérifiés par SMS (#105),
                    éditables si SMS désactivé (#107) */}
                <div className="col-md-6">
                  <label className="form-label">
                    {t('register.phone')} <span className="text-danger">*</span>
                    {appConfig.sms_enabled && (
                      <span className="badge bg-success ms-2">
                        <i className="bi bi-check-circle me-1"></i>
                        {t('preReg.verified', 'Vérifié')}
                      </span>
                    )}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={appConfig.sms_enabled}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    {t('register.email')} <span className="text-danger">*</span>
                    <span className="badge bg-success ms-2">
                      <i className="bi bi-check-circle me-1"></i>
                      {t('preReg.verified', 'Vérifié')}
                    </span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    disabled
                    required
                  />
                  <small className="text-muted">{t('register.emailAsLogin')}</small>
                </div>

                {/* Password */}
                <div className="col-md-6">
                  <label className="form-label">{t('register.password')} <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isDisabled}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                  {password && (
                    <div className="password-rules mt-2">
                      <small className={passwordRules.minLength ? 'text-success' : 'text-danger'}>
                        <i className={`bi ${passwordRules.minLength ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                        {t('register.passwordRules.minLength')}
                      </small><br />
                      <small className={passwordRules.hasUppercase ? 'text-success' : 'text-danger'}>
                        <i className={`bi ${passwordRules.hasUppercase ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                        {t('register.passwordRules.uppercase')}
                      </small><br />
                      <small className={passwordRules.hasDigit ? 'text-success' : 'text-danger'}>
                        <i className={`bi ${passwordRules.hasDigit ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                        {t('register.passwordRules.alphanumeric')}
                      </small>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="col-md-6">
                  <label className="form-label">{t('register.confirmPassword')} <span className="text-danger">*</span></label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isDisabled}
                    required
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <small className="text-danger mt-1 d-block">
                      <i className="bi bi-x-circle-fill me-1"></i>
                      {t('register.errors.passwordMismatch')}
                    </small>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <small className="text-success mt-1 d-block">
                      <i className="bi bi-check-circle-fill me-1"></i>
                      {t('register.passwordsMatch')}
                    </small>
                  )}
                </div>
              </div>

              <button
                className="btn btn-primary w-100 mt-4"
                onClick={handleContinue}
                disabled={!first_name || !last_name || !email || !phone || !password || !isPasswordValid || password !== confirmPassword || validatingStep1}
              >
                {validatingStep1 ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                ) : (
                  <i className="bi bi-arrow-right ms-2"></i>
                )}
                {t('register.continue')}
              </button>

              <p className="text-center mt-3 mb-0">
                {t('auth.hasAccount')} <Link to={`/${i18n.language}/login`}>{t('auth.signIn')}</Link>
              </p>
            </>
          )}

          {/* STEP 2: Select Plan & Payment */}
          {step === 2 && (
            <>
              <h2 className="text-center mb-4">
                {t('register.chooseFormula')}
              </h2>

              {isTestMode && testPackage ? (
                <div className="alert alert-info text-center animate-on-scroll fade-in-up mb-4" role="alert">
                  <h5><i className="bi bi-stars me-2"></i> {t('register.testModeTitle', 'Vous testez l\'application berti-law.net')}</h5>
                  <p className="mb-0">
                    {t('register.testModeDesc', 'Vous avez sélectionné le pack de test gratuit :')} <strong>{isArabic ? testPackage.nameAr : testPackage.name}</strong>
                  </p>
                  <div className="mt-2 text-muted small">
                    <i className="bi bi-check-circle-fill text-success me-1"></i>
                    {testPackage.questionsCount ? `${testPackage.questionsCount} questions` : 'Accès limité'}
                  </div>
                </div>
              ) : (
                <PackageSelector
                  selectedPlan={selectedPlan}
                  onPlanSelect={(id) => {
                    setSelectedPlan(id);
                    // Reset nbPoste when switching packages
                    const pkg = allPackages.find(p => p.id === id);
                    if (!pkg?.isNbPosteEnabled) setNbPoste(null);
                  }}
                  billingPeriod={billingPeriod}
                  onBillingPeriodChange={setBillingPeriod}
                  disabled={isDisabled}
                  onPackagesLoaded={setAllPackages}
                />
              )}

              {/* Nombre de postes - only for enterprise packages */}
              {selectedPlan && allPackages.find(p => p.id === selectedPlan)?.isNbPosteEnabled && (
                <div className="nb-poste-section mt-4 mb-4">
                  <label className="form-label">
                    <i className="bi bi-pc-display-horizontal me-2"></i>
                    {t('register.nbPoste', 'Nombre de postes')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    value={nbPoste || ''}
                    onChange={(e) => setNbPoste(e.target.value ? Math.max(1, parseInt(e.target.value)) : null)}
                    placeholder={t('register.nbPostePlaceholder', 'Ex: 5, 10, 20...')}
                    disabled={isDisabled}
                    required
                  />
                  <small className="text-muted">
                    {t('register.nbPosteHelp', 'Nombre de connexions simultanées autorisées pour votre entreprise')}
                  </small>
                </div>
              )}

              {/* Payment Methods Section */}
              {!isTestMode && (
                <section className="payment-section mt-4 mb-4">
                  <h5 className="section-title text-center mb-3">
                    <i className="bi bi-wallet2 me-2 text-success"></i>
                    {t('register.paymentMethods')}
                  </h5>
                  <div className="payment-methods">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`payment-method ${selectedPaymentMethod === method.id ? 'selected' : ''}`}
                        onClick={() => setSelectedPaymentMethod(method.id)}
                      >
                        <i className={`bi ${method.icon}`}></i>
                        <span>{isArabic ? method.nameAr : method.name}</span>
                        {selectedPaymentMethod === method.id && (
                          <i className="bi bi-check-circle-fill ms-auto text-success"></i>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Terms Section */}
              <section className="terms-section mb-4">
                <div className={`terms-card ${termsError ? 'has-error' : ''}`}>
                  <h5>
                    <i className="bi bi-shield-check me-2"></i>
                    {isTestMode ? t('register.testTermsTitle') : t('register.termsTitle')}
                  </h5>
                  <ul className="terms-list">
                    {isTestMode ? (
                      <>
                        <li>{t('register.testTerms.t1')}</li>
                        <li>{t('register.testTerms.t2')}</li>
                        <li>{t('register.testTerms.t3')}</li>
                        <li>{t('register.testTerms.t4')}</li>
                        <li>{t('register.testTerms.t5')}</li>
                        <li>{t('register.testTerms.t6')}</li>
                        <li>{t('register.testTerms.t7')}</li>
                        <li>{t('register.testTerms.t8')}</li>
                      </>
                    ) : (
                      <>
                        <li>{t('register.terms.accuracy')}</li>
                        <li>{t('register.terms.personal')}</li>
                        <li>{t('register.terms.codes')}</li>
                        <li>{t('register.terms.updates')}</li>
                        <li>{t('register.terms.ai')}</li>
                      </>
                    )}
                  </ul>
                </div>
              </section>

              {/* CGV   */}
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="acceptCgv"
                  checked={acceptedCgv}
                  onChange={(e) => setAcceptedCgv(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="acceptCgv" dangerouslySetInnerHTML={{ __html: t('register.acceptCgv') }} />
              </div>

              <div className="d-flex gap-2 mt-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setStep(1)}
                  disabled={isDisabled}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  {t('register.back')}
                </button>
                <button
                  onClick={handleRegister}
                  className="btn btn-success flex-grow-1"
                  disabled={isDisabled || !selectedPlan || !acceptedCgv}
                >
                  {t('auth.registerButton')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Register;

